import React from 'react';
import { Clock, MapPin, Edit, Trash2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const AppointmentsList = ({ appointments = [], onEdit, onDelete }) => {
  const serviceTypeColors = {
    daycare: 'bg-service-daycare border-yellow-300',
    sitting: 'bg-service-sitting border-purple-300',
    walking: 'bg-service-walking border-blue-300',
    checkin: 'bg-service-checkin border-green-300',
  };

  const serviceTypeLabels = {
    daycare: 'Doggy Day Care',
    sitting: 'Pet Sitting',
    walking: 'Dog Walking',
    checkin: 'Check In',
  };

  if (appointments.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-brand-brown/60">No upcoming appointments</p>
        <p className="text-sm text-brand-brown/40 mt-2">
          Add your first appointment to get started!
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 max-h-96 overflow-y-auto scrollbar-thin">
      {appointments.map((appointment) => {
        const appointmentDate = typeof appointment.date === 'string' 
          ? parseISO(appointment.date) 
          : appointment.date;

        return (
          <div
            key={appointment.id}
            className={`
              p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-md
              ${serviceTypeColors[appointment.serviceType] || 'bg-white border-gray-200'}
            `}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-semibold text-brand-brown mb-1">
                  {appointment.petName}
                </h3>
                <p className="text-sm text-brand-brown/70 mb-2">
                  {serviceTypeLabels[appointment.serviceType]}
                </p>
                
                <div className="flex flex-col space-y-1 text-sm text-brand-brown/60">
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4" />
                    <span>
                      {format(appointmentDate, 'MMM d, yyyy')} at {appointment.time}
                    </span>
                  </div>
                  
                  {appointment.location && (
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4" />
                      <span>{appointment.location}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-start space-x-2 ml-4">
                {onEdit && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(appointment);
                    }}
                    className="p-2 rounded-lg hover:bg-white/80 text-pastel-blue transition-colors"
                    title="Edit"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(appointment.id);
                    }}
                    className="p-2 rounded-lg hover:bg-white/80 text-red-500 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
                {appointment.clientName && (
                  <div className="text-right">
                    <p className="text-sm font-medium text-brand-brown">
                      {appointment.clientName}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {appointment.notes && (
              <div className="mt-3 pt-3 border-t border-brand-brown/10">
                <p className="text-sm text-brand-brown/60 italic">
                  {appointment.notes}
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AppointmentsList;
